// src/pages/storybooks/StorybooksDetail.tsx
import { useCallback, useEffect, useMemo, useRef, useState, ChangeEvent, MouseEvent, ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { getPublicImageUrl, getSignedImageUrl } from "@/lib/signedUrl";
import { useScrollLock } from "@/hooks/useScrollLock";
import { jsonFetch } from "@/utils/jsonFetch";
import { useStorybookContext, StorybookClassroom } from "./useStorybookContext";
import CharactersModal from "./CharactersModal";
import {
  v2Initialize,
  v2GenerateNarrative,
  getJobStatus,
  generateText as sbGenerateText,
  renderPdf as sbRenderPdf,
  updateSettings as sbUpdateSettings,
  generateIllustration as sbGenerateIllustration,
  publishStorybook as sbPublishStorybook,
  storybooksApi as sbFetch,
} from "@/lib/storybooksApi";

// Type definitions
interface StorybookPage {
  pageId?: string | null;
  page_id?: string | null;
  id?: string | null;
  pageNumber?: number;
  page_no?: number;
  text?: string;
  caption?: string;
  prompt?: string;
  image_url?: string | null;
  imageUrl?: string | null;
  image_path?: string | null;
  imagePath?: string | null;
  public_image_url?: string | null;
  layout?: string | null;
  characters?: unknown;
  last_error?: string | null;
  illustration_ready?: boolean;
  image_updated_at?: string | null;
  updated_at?: string | null;
  version?: number;
}

interface StorybookSettings {
  pageCount?: number;
  illustrationMode?: string;
  renderMode?: string;
  grade_level?: string;
  teacher_notes?: string;
  [key: string]: unknown;
}

interface RosterEntry {
  id?: string | null;
  full_name?: string;
  grade?: string;
  nickname?: string;
  role?: string;
  traits?: string | string[];
  type?: string;
}

interface Storybook {
  id: string;
  classroom_id?: string;
  title: string;
  grade_level?: string;
  grade?: string;
  teacher_name?: string;
  teacher_notes?: string;
  narrative?: string;
  pages: StorybookPage[];
  include_illustrations?: boolean;
  status?: string;
  pdf_url?: string | null;
  updated_at?: string;
  created_at?: string;
  published_at?: string;
  student_profiles?: RosterEntry[];
  settings?: StorybookSettings;
}

interface RenderJob {
  id?: string;
  job_id?: string;
  status?: string;
  completed_pages?: number;
  total_pages?: number;
  failed_pages?: number;
  progress_pct?: number;
  percent?: number;
  last_error?: string | null;
  notes?: JobNote[];
  terminal?: boolean;
  updated_at?: string;
}

interface JobNote {
  error?: string;
  message?: string;
  page_no?: number;
  page?: number;
}

interface JobSummary {
  id: string | null;
  status: string;
  percent: number;
  completed: number;
  total: number;
  failed: number;
  notes: JobNote[];
  lastError: string | null;
  updatedAt: string | null;
}

interface Toast {
  id: string;
  message: string;
  kind: string;
}

interface Student {
  id?: string;
  student_id?: string;
  participant_id?: string;
  full_name?: string;
  name?: string;
  display_name?: string;
  grade?: string;
  nickname?: string;
  role?: string;
  traits?: string | string[];
  descriptors?: string[];
  notes?: string[];
  profile?: {
    traits?: string[];
    descriptors?: string[];
    notes?: string[];
  } | null;
}

interface ConsentState {
  [key: string]: boolean;
}

interface PageBusyState {
  [pageNumber: number]: boolean;
}

interface PageThumbnail {
  key: string;
  pageNumber: number;
  page: StorybookPage;
  image: string | null;
  version: number;
}

interface ActiveProgress {
  status: string;
  progress: number;
  percent?: number;
  completed_pages: number;
  failed_pages: number;
  total_pages: number;
  last_error: string | null;
}

interface JobSummaryDetails {
  tone: string;
  title: string;
  description: string;
  data: JobSummary;
}

interface UseStorybookImageParams {
  key: string | null;
  publicKey?: string | null;
  usePublic?: boolean;
  refreshTick?: number;
  updatedAt?: string | null;
  expiresIn?: number;
}

interface UploadTarget {
  signedUrl?: string;
  key?: string;
  publicUrl?: string;
}

interface ApiPageEntry {
  page_no: number;
  text: string;
  page_id?: string | null;
  prompt?: string;
  layout?: string | null;
  characters?: unknown;
  image_url?: string;
  image_path?: string;
}

interface UploadRequestResponse {
  uploads?: UploadTarget[];
  participant_id?: string | null;
}

interface UploadConfirmResponse {
  uploaded?: UploadTarget[];
}

interface TeacherStudentsResponse {
  students?: Student[];
}

interface GenerateTextResponse {
  pages?: StorybookPage[];
}

interface GenerateIllustrationsResponse {
  job_id?: string;
}

interface RenderPdfResponse {
  url?: string;
  signedUrl?: string;
  pdf_url?: string;
  pdfUrl?: string;
}

interface GenerateIllustrationResponse {
  page?: StorybookPage;
  image_url?: string;
  imageUrl?: string;
  image_path?: string;
  page_id?: string;
  pageId?: string;
  version?: number;
  image_updated_at?: string;
}

interface PublishStorybookResponse {
  status?: string;
  publishedAt?: string;
}

interface StoryPageRow {
  id?: string | null;
  page_no?: number | null;
  text?: string | null;
  caption?: string | null;
  image_url?: string | null;
  public_image_url?: string | null;
  image_path?: string | null;
  layout?: string | null;
  illustration_ready?: string | boolean | null;
  image_updated_at?: string | null;
  updated_at?: string | null;
  version?: string | number | null;
  last_error?: string | null;
}

interface SanitizedPageBase {
  pageId: string | null;
  pageNumber: number;
  text: string;
  caption: string;
  prompt: string;
  imageUrl: string | null;
  image_url: string | null;
  image_path?: string | null;
  public_image_url?: string | null;
  layout?: string | null;
  characters?: unknown;
  last_error?: string | null;
}

const CONSENT_ITEMS = [
  { key: "parental", label: "I have parental or guardian consent to share these classroom moments." },
  { key: "facesBlurred", label: "Student faces are blurred or intentionally framed out." },
  { key: "noNames", label: "No student last names, addresses, or personal details appear in the story." },
  { key: "schoolApproval", label: "My school leader has approved sharing this story externally." },
];

const DEFAULT_SETTINGS: StorybookSettings = {
  pageCount: 6,
  illustrationMode: "full",
  renderMode: "batch",
};

const PAGE_COUNT_OPTIONS = [6, 8, 10];

const ILLUSTRATION_OPTIONS = [
  { value: "none", label: "No illustrations" },
  { value: "light", label: "Light illustrations" },
  { value: "full", label: "Full illustrations" },
];

const RENDER_MODES = [
  { value: "batch", label: "One-button flow" },
  { value: "per-page", label: "Page by page" },
];

const AUTOSAVE_MS = 700;

const SUCCESS_STATUSES = new Set(["complete"]);
const PARTIAL_STATUSES = new Set(["complete-with-errors"]);
const FAILED_STATUSES = new Set(["failed", "error"]);
const CANCELLED_STATUSES = new Set(["cancelled", "canceled"]);
const TERMINAL_STATUSES = new Set([
  ...SUCCESS_STATUSES,
  ...PARTIAL_STATUSES,
  ...FAILED_STATUSES,
  ...CANCELLED_STATUSES,
]);

const isHttpUrl = (value: unknown): value is string => (typeof value === "string" ? /^https?:\/\//i.test(value.trim()) : false);
const pickStorageKey = (...values: (string | null | undefined)[]): string | null => {
  for (const value of values) {
    if (typeof value === "string" && value && !isHttpUrl(value)) {
      return value;
    }
  }
  return null;
};
const pickRemoteUrl = (...values: (string | null | undefined)[]): string | null => {
  for (const value of values) {
    if (isHttpUrl(value)) {
      return value.trim();
    }
  }
  return null;
};

function useDebounced<T extends (...args: unknown[]) => void>(fn: T, ms = 300): (...args: Parameters<T>) => void {
  const fnRef = useRef<T>(fn);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return useCallback((...args: Parameters<T>) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      fnRef.current?.(...args);
    }, ms);
  }, [ms]);
}

function useStorybookImage({
  key,
  publicKey,
  usePublic = false,
  refreshTick = 0,
  updatedAt = null,
  expiresIn = 600,
}: UseStorybookImageParams): string | null {
  const [src, setSrc] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    (async () => {
      try {
        const targetKey = usePublic && publicKey ? publicKey : key;
        if (!targetKey) {
          if (!cancelled) setSrc(null);
          return;
        }

        let url = null;
        if (usePublic && publicKey) {
          url = getPublicImageUrl(publicKey);
        } else {
          url = await getSignedImageUrl(targetKey, expiresIn);
        }

        if (!url) {
          if (!cancelled) setSrc(null);
          return;
        }

        const bustSeed = updatedAt
          ? String(updatedAt)
          : refreshTick
          ? `tick-${refreshTick}`
          : nonce
          ? `nonce-${nonce}`
          : null;
        const finalUrl =
          bustSeed && url
            ? `${url}${url.includes("?") ? "&" : "?"}t=${encodeURIComponent(bustSeed)}`
            : url;

        if (!cancelled) {
          setSrc(finalUrl);
          if (!usePublic) {
            const refreshDelaySeconds = Math.max(60, Math.max(1, expiresIn - 60));
            timerRef.current = setTimeout(() => {
              setNonce((value) => value + 1);
            }, refreshDelaySeconds * 1000);
          }
        }
      } catch (error) {
        console.error("[Storybooks] failed to build image url", error);
        if (!cancelled) setSrc(null);
      }
    })();

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [key, publicKey, usePublic, refreshTick, updatedAt, expiresIn, nonce]);

  return src;
}

interface PagePreviewProps {
  page: StorybookPage | null;
  refreshTick?: number;
  imgClassName?: string;
  placeholderClassName?: string;
  placeholderContent?: ReactNode;
  usePublic?: boolean;
}

function PagePreview({
  page,
  refreshTick,
  imgClassName = "",
  placeholderClassName = "",
  placeholderContent = null,
  usePublic = false,
}: PagePreviewProps) {
  const storageKey = pickStorageKey(
    page?.image_url,
    page?.imageUrl,
    page?.image_path,
    page?.imagePath
  );
  const publicKey = pickStorageKey(page?.public_image_url);
  const remoteSrc = pickRemoteUrl(
    page?.image_url,
    page?.imageUrl,
    page?.image_path,
    page?.imagePath
  );
  const signedSrc = useStorybookImage({
    key: storageKey,
    publicKey,
    usePublic,
    refreshTick,
    updatedAt: page?.image_updated_at || page?.updated_at || null,
    expiresIn: 600,
  });
  const altText = `Page ${page?.pageNumber ?? page?.page_no ?? ""}`;

  if (signedSrc) {
    return <img src={signedSrc} alt={altText} className={imgClassName} />;
  }

  if (remoteSrc) {
    return <img src={remoteSrc} alt={altText} className={imgClassName} />;
  }

  const wrapperClass = placeholderClassName || imgClassName;
  if (placeholderContent) {
    return <div className={wrapperClass}>{placeholderContent}</div>;
  }

  return (
    <div className={wrapperClass}>
      <div className="flex h-full w-full items-center justify-center text-sm text-cocoa/60">
        Illustration not generated yet.
      </div>
    </div>
  );
}

interface ConsentModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  consent: ConsentState;
  setConsent: React.Dispatch<React.SetStateAction<ConsentState>>;
}

function ConsentModal({ open, onClose, onConfirm, consent, setConsent }: ConsentModalProps) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-xl">
        <h3 className="text-xl font-semibold text-cocoa">Consent checklist</h3>
        <p className="mt-2 text-sm text-cocoa/70">
          Confirm each item below before publishing to the public classroom stories feed.
        </p>
        <div className="mt-4 space-y-3">
          {CONSENT_ITEMS.map((item) => (
            <label key={item.key} className="flex items-start gap-3 text-sm text-cocoa">
              <input
                type="checkbox"
                className="mt-1 rounded border-sand text-brick focus:ring-brick"
                checked={Boolean(consent[item.key])}
                onChange={(event) =>
                  setConsent((prev) => ({ ...prev, [item.key]: event.target.checked }))
                }
              />
              <span>{item.label}</span>
            </label>
          ))}
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            onClick={onConfirm}
            disabled={CONSENT_ITEMS.some((item) => !consent[item.key])}
          >
            Publish storybook
          </button>
        </div>
      </div>
    </div>
  );
}

interface PhotosPanelProps {
  classroomId: string;
  bookId: string;
  onUploaded?: (items: UploadTarget[]) => void;
}

const PHOTOS_CORE_API = import.meta.env.VITE_CORE_API_URL || '';

function PhotosPanel({ classroomId, bookId, onUploaded }: PhotosPanelProps) {
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const next = Array.from(event.target.files || []);
    setFiles(next);
  };

  const upload = async () => {
    if (!files.length || !classroomId) return;
    setBusy(true);
    try {
      const request = await jsonFetch(`${PHOTOS_CORE_API}/api/v1/teacher/photos/upload`, {
        method: "POST",
        body: JSON.stringify({
          classroom_id: classroomId,
          book_id: bookId,
          files: files.map((file) => ({ name: file.name, type: file.type })),
        }),
      }) as UploadRequestResponse;

      const uploads: UploadTarget[] = Array.isArray(request?.uploads) ? request.uploads : [];
      if (!uploads.length) throw new Error("Upload session failed");

      await Promise.all(
        uploads.map((target: UploadTarget, index: number) => {
          const file = files[index];
          if (!target?.signedUrl || !file) return Promise.resolve();
          return fetch(target.signedUrl, {
            method: "PUT",
            headers: file.type ? { "Content-Type": file.type } : undefined,
            body: file,
          }).then((response) => {
            if (!response.ok) {
              throw new Error(`Upload failed for ${file.name}`);
            }
            return response;
          });
        })
      );

      const confirm = await jsonFetch(`${PHOTOS_CORE_API}/api/v1/teacher/photos/upload`, {
        method: "POST",
        body: JSON.stringify({
          classroom_id: classroomId,
          book_id: bookId,
          participant_id: request?.participant_id || null,
          uploaded: uploads.map((target: UploadTarget) => ({ key: target.key, url: target.publicUrl })),
        }),
      }) as UploadConfirmResponse;

      onUploaded?.(Array.isArray(confirm?.uploaded) ? confirm.uploaded : []);
      setFiles([]);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    } catch (error) {
      alert(error instanceof Error ? error.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 rounded-3xl border border-sand bg-white p-4 shadow-soft">
      <div>
        <h3 className="text-lg font-semibold text-cocoa">Class photos (optional)</h3>
        <p className="text-sm text-cocoa/70">
          Add a few clear, front-facing shots. We’ll use them to keep characters consistent.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input ref={inputRef} type="file" multiple accept="image/*" onChange={onPick} />
        <button
          type="button"
          className="btn-secondary"
          onClick={upload}
          disabled={busy || !files.length}
        >
          {busy ? "Uploading…" : `Upload ${files.length || ""}`}
        </button>
      </div>
    </div>
  );
}

interface JobProgressModalProps {
  open: boolean;
  job: RenderJob | null;
  onClose: () => void;
}

function JobProgressModal({ open, job, onClose }: JobProgressModalProps) {
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const statusValue = (job?.status || 'idle').toString().toLowerCase();
  const heading = statusValue ? statusValue.toUpperCase() : 'IN PROGRESS';
  const completedRaw = Number(job?.completed_pages ?? 0);
  const totalRaw = Number(job?.total_pages ?? 0);
  const completed = Number.isFinite(completedRaw) ? Math.max(0, completedRaw) : 0;
  const total = Number.isFinite(totalRaw) ? Math.max(0, totalRaw) : 0;
  const failedRaw = Number(job?.failed_pages ?? 0);
  const failed = Number.isFinite(failedRaw) ? Math.max(0, failedRaw) : 0;
  const percent = (() => {
    const pct = Number(job?.percent);
    if (Number.isFinite(pct)) {
      return Math.max(0, Math.min(100, Math.round(pct)));
    }
    if (total > 0) {
      return Math.max(0, Math.min(100, Math.round((completed / total) * 100)));
    }
    const legacy = Number(job?.progress_pct ?? 0);
    return Number.isFinite(legacy) ? Math.max(0, Math.min(100, Math.round(legacy))) : 0;
  })();
  const notes = Array.isArray(job?.notes) ? job.notes : [];
  const terminal = job?.terminal === true || TERMINAL_STATUSES.has(statusValue);
  const hasError = typeof job?.last_error === 'string' && job.last_error.trim().length > 0;
  const message = hasError
    ? job.last_error
    : statusValue === 'queued'
    ? 'Waiting for a render slot…'
    : terminal && failed > 0
    ? `${failed} page${failed === 1 ? '' : 's'} need attention.`
    : terminal
    ? 'Illustrations finished.'
    : 'Rendering your illustrations…';

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  return (
    <div
      className="sb-modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className="sb-modal relative w-full max-w-md space-y-4 rounded-3xl bg-white p-6 text-center shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-live="polite"
      >
        <button
          type="button"
          className="absolute right-4 top-4 rounded-full bg-shell px-3 py-1 text-sm text-cocoa/70 transition hover:bg-sand"
          onClick={() => onClose?.()}
        >
          Close
        </button>
        <p className="text-xs font-semibold uppercase tracking-wide text-cocoa/50">{heading}</p>
        <div className="h-2 w-full overflow-hidden rounded-full bg-shell">
          <div
            className="h-full rounded-full bg-brick transition-[width] duration-500"
            style={{ width: `${percent}%` }}
          />
        </div>
        <p className="text-2xl font-semibold text-cocoa">{percent}%</p>
        <p className="text-sm font-medium text-cocoa/70">
          {`${Math.max(0, Math.round(completed))}/${Math.max(0, Math.round(total))} pages`}
          {failed > 0 ? ` • ${failed} issue${failed === 1 ? '' : 's'}` : ''}
        </p>
        <p className={`text-sm ${hasError ? 'text-rose-600' : terminal && failed > 0 ? 'text-amber-600' : 'text-cocoa/70'}`}>
          {message}
        </p>
        {notes.length > 0 && (
          <div className="rounded-2xl bg-shell/70 p-3 text-left text-xs text-cocoa/70">
            <p className="font-semibold text-cocoa">Notes</p>
            <ul className="mt-1 space-y-1">
              {notes.slice(0, 5).map((note, idx) => {
                const label = typeof note === 'string' ? note : note?.error || note?.message;
                const page = note?.page_no || note?.page || null;
                return (
                  <li key={idx} className="flex items-start gap-2">
                    {page ? <span className="font-semibold text-cocoa">p.{page}</span> : null}
                    <span className="flex-1 text-cocoa/70">{label || 'Issue recorded'}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

interface StoryPreviewModalProps {
  open: boolean;
  onClose: () => void;
  pages?: StorybookPage[];
  initialIndex?: number;
  title?: string;
  refreshTick?: number;
  progress?: ActiveProgress | null;
}

function StoryPreviewModal({
  open,
  onClose,
  pages = [],
  initialIndex = 0,
  title = "Story preview",
  refreshTick = 0,
  progress = null,
}: StoryPreviewModalProps) {
  useScrollLock(open);
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) {
      setIndex(initialIndex);
    }
  }, [initialIndex, open]);

  if (!open) return null;

  const safePages = Array.isArray(pages) ? pages : [];
  const current = safePages[index] || null;

  const goto = (next: number) => {
    const max = safePages.length - 1;
    const clamped = Math.max(0, Math.min(max, next));
    setIndex(clamped);
  };

  const percent = Math.max(
    0,
    Math.min(100, Number(progress?.progress ?? progress?.percent ?? 0) || 0)
  );
  const statusLabel = progress?.status ? progress.status.toUpperCase() : null;
  const failedCount = Number.isFinite(Number(progress?.failed_pages))
    ? Math.max(0, Number(progress?.failed_pages))
    : 0;
  const progressMessage =
    typeof progress?.last_error === 'string' && progress.last_error
      ? progress.last_error
      : progress?.status === 'queued'
      ? 'Waiting for a render slot…'
      : failedCount > 0
      ? `${failedCount} page${failedCount === 1 ? '' : 's'} need attention.`
      : 'Rendering illustrations…';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6">
      <div className="flex w-full max-w-5xl flex-col gap-4 rounded-3xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-cocoa">{title}</h3>
            <p className="text-xs text-cocoa/60">
              Page {index + 1} of {safePages.length || 1}
            </p>
          </div>
          <button
            type="button"
            className="rounded-full bg-shell px-3 py-1 text-sm text-cocoa/70 hover:bg-sand"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        {progress && (
          <div className="space-y-2 rounded-2xl bg-shell/60 p-3">
            <div className="flex items-center justify-between text-xs font-medium text-cocoa/70">
              <span>{statusLabel || 'IN PROGRESS'}</span>
              <span>
                {percent}%
                {failedCount > 0 ? ` • ${failedCount} issue${failedCount === 1 ? '' : 's'}` : ''}
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-shell">
              <div
                className="h-2 rounded-full bg-amber-500 transition-[width] duration-500"
                style={{ width: `${percent}%` }}
              />
            </div>
            <div
              className={`text-xs ${
                typeof progress?.last_error === 'string' && progress.last_error
                  ? 'text-rose-600'
                  : 'text-cocoa/60'
              }`}
            >
              {progressMessage}
            </div>
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-[2fr,1fr]">
          <div className="space-y-3">
            <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border border-sand bg-shell">
              <PagePreview
                page={current}
                refreshTick={refreshTick}
                imgClassName="h-full w-full object-cover"
                placeholderClassName="flex h-full w-full items-center justify-center text-sm text-cocoa/60"
              />
            </div>
            <div className="rounded-2xl border border-sand bg-shell/60 p-4">
              <p className="font-semibold text-cocoa">Page text</p>
              <p className="mt-2 whitespace-pre-wrap text-sm text-cocoa/80">
                {current?.text || "No text yet."}
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-cocoa">Pages</p>
            <div className="max-h-[60vh] space-y-2 overflow-auto pr-1">
              {safePages.map((page, idx) => {
                const pageKey = page?.pageId || page?.page_id || `preview-${idx}`;
                const active = idx === index;
                return (
                  <button
                    key={pageKey}
                    type="button"
                    onClick={() => goto(idx)}
                    className={`w-full rounded-2xl border px-3 py-2 text-left text-sm transition ${
                      active
                        ? "border-brick bg-rose-50 text-brick"
                        : "border-sand bg-shell hover:border-brick/40"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">Page {page.pageNumber || page.page_no || idx + 1}</span>
                      {(page.illustration_ready ||
                        page.image_url ||
                        page.imageUrl ||
                        page.image_path ||
                        page.imagePath) && (
                        <span className="text-xs text-cocoa/60">Illustrated</span>
                      )}
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs text-cocoa/70">{page.text || ""}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => goto(index - 1)}
            disabled={index <= 0}
          >
            Previous
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => goto(index + 1)}
            disabled={index >= safePages.length - 1}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StorybooksDetail() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const {
    enabled,
    classroom: rawClassroom,
    featureEnabled,
    loading: contextLoading,
    error: contextError,
    refresh,
  } = useStorybookContext();
  const classroom = rawClassroom as StorybookClassroom | null;

  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [storyError, setStoryError] = useState<Error | null>(null);
  const [storybook, setStorybook] = useState<Storybook | null>(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentState, setConsentState] = useState<ConsentState>({});
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<RenderJob | null>(null);
  const [jobSummary, setJobSummary] = useState<JobSummary | null>(null);
  const [showProgress, setShowProgress] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [charactersOpen, setCharactersOpen] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState<string | null>(null);
  const [pageBusy, setPageBusy] = useState<PageBusyState>({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [gradeDraft, setGradeDraft] = useState("");
  const [notesDraft, setNotesDraft] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  const isMountedRef = useRef(true);
  const jobActiveRef = useRef(false);
  const prevJobStatusRef = useRef<string>('idle');
  const pageVersionRef = useRef<Map<number, number>>(new Map());

  const bookId = useMemo(() => (routeId || "").trim(), [routeId]);
  const storageKey = useMemo(
    () => (bookId ? `storybook:${bookId}:job_id` : null),
    [bookId]
  );

  const jobStatus = useMemo(
    () => (job?.status || "idle").toString().toLowerCase(),
    [job?.status]
  );
  const jobCompletedPages = useMemo(() => {
    const raw = Number(job?.completed_pages ?? 0);
    return Number.isFinite(raw) ? Math.max(0, raw) : 0;
  }, [job?.completed_pages]);
  const jobTotalPages = useMemo(() => {
    const raw = Number(job?.total_pages ?? 0);
    return Number.isFinite(raw) ? Math.max(0, raw) : 0;
  }, [job?.total_pages]);
  const jobFailedPages = useMemo(() => {
    const raw = Number(job?.failed_pages ?? 0);
    return Number.isFinite(raw) ? Math.max(0, raw) : 0;
  }, [job?.failed_pages]);
  const jobNotes = useMemo(() => (Array.isArray(job?.notes) ? job.notes : []), [job?.notes]);
  const jobPercent = useMemo(() => {
    const pctRaw = Number(job?.percent);
    if (Number.isFinite(pctRaw)) {
      return pctRaw;
    }
    if (jobTotalPages > 0) {
      return Math.round((jobCompletedPages / jobTotalPages) * 100);
    }
    const legacy = Number(job?.progress_pct ?? 0);
    return Number.isFinite(legacy) ? legacy : 0;
  }, [job?.percent, jobCompletedPages, jobTotalPages, job?.progress_pct]);
  const jobPercentDisplay = useMemo(
    () => Math.max(0, Math.min(100, Math.round(jobPercent))),
    [jobPercent]
  );
  const jobLastError = useMemo(
    () => (typeof job?.last_error === "string" ? job.last_error : null),
    [job?.last_error]
  );
  const jobTerminal = useMemo(
    () => Boolean(job?.terminal) || TERMINAL_STATUSES.has(jobStatus),
    [job?.terminal, jobStatus]
  );
  const jobIsActive = !jobTerminal && (jobStatus === "queued" || jobStatus === "running");
  const activeProgress = useMemo(
    () =>
      jobIsActive
        ? {
            status: jobStatus,
            progress: jobPercentDisplay,
            completed_pages: jobCompletedPages,
            failed_pages: jobFailedPages,
            total_pages: jobTotalPages,
            last_error: jobLastError,
          }
        : null,
    [
      jobIsActive,
      jobStatus,
      jobPercentDisplay,
      jobCompletedPages,
      jobFailedPages,
      jobTotalPages,
      jobLastError,
    ]
  );

  const buildJobSummary = useCallback((snapshot: RenderJob | null): JobSummary | null => {
    if (!snapshot) return null;
    const statusValue = (snapshot.status || '').toString().toLowerCase();
    if (!TERMINAL_STATUSES.has(statusValue)) return null;

    const completed = Number.isFinite(Number(snapshot.completed_pages))
      ? Math.max(0, Math.round(Number(snapshot.completed_pages)))
      : 0;
    const total = Number.isFinite(Number(snapshot.total_pages))
      ? Math.max(0, Math.round(Number(snapshot.total_pages)))
      : 0;
    const failed = Number.isFinite(Number(snapshot.failed_pages))
      ? Math.max(0, Math.round(Number(snapshot.failed_pages)))
      : 0;
    let percent = Number.isFinite(Number(snapshot.percent))
      ? Math.max(0, Math.min(100, Math.round(Number(snapshot.percent))))
      : 0;
    if (!percent && total > 0) {
      percent = Math.max(0, Math.min(100, Math.round((completed / total) * 100)));
    }
    const notesPayload = Array.isArray(snapshot.notes) ? snapshot.notes : [];

    return {
      id: snapshot.id || snapshot.job_id || null,
      status: statusValue,
      percent,
      completed,
      total,
      failed,
      notes: notesPayload,
      lastError: typeof snapshot.last_error === 'string' ? snapshot.last_error : null,
      updatedAt: snapshot.updated_at || null,
    };
  }, []);

  const isPublished = storybook?.status === "published";
  const jobSummaryDetails = useMemo(() => {
    if (!jobSummary) return null;
    const status = jobSummary.status || 'complete';
    let tone = 'info';
    let title = 'Illustrations update';
    let description = '';

    if (SUCCESS_STATUSES.has(status)) {
      tone = 'success';
      title = 'Illustrations ready';
      description = `${jobSummary.completed}/${jobSummary.total} pages illustrated.`;
    } else if (PARTIAL_STATUSES.has(status)) {
      tone = 'warn';
      title = 'Illustrations finished with issues';
      description = `${jobSummary.failed} page${jobSummary.failed === 1 ? '' : 's'} need attention.`;
    } else if (FAILED_STATUSES.has(status)) {
      tone = 'error';
      title = 'Illustration job failed';
      description = jobSummary.lastError || 'Please try again when you are ready.';
    } else if (CANCELLED_STATUSES.has(status)) {
      tone = 'warn';
      title = 'Illustration job cancelled';
      description = 'You can start another render whenever you are ready.';
    }

    return { tone, title, description, data: jobSummary };
  }, [jobSummary]);
  const summaryToneClasses = useMemo(
    (): Record<string, string> => ({
      success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
      warn: 'border-amber-200 bg-amber-50 text-amber-900',
      error: 'border-rose-200 bg-rose-50 text-rose-900',
      info: 'border-sky-200 bg-sky-50 text-sky-900',
    }),
    []
  );
  const canSubmit = storybook && storybook.status && ["draft", "submitted", "approved"].includes(storybook.status);

  const settings = useMemo(
    () => ({ ...DEFAULT_SETTINGS, ...(storybook?.settings || {}) }),
    [storybook?.settings]
  );
  const roster = useMemo(
    () => (Array.isArray(storybook?.student_profiles) ? storybook.student_profiles : []),
    [storybook?.student_profiles]
  );

  const teacherMetadata = session?.user?.user_metadata || {};
  const teacherRosterId = session?.user?.id || null;
  const teacherFullName =
    storybook?.teacher_name ||
    teacherMetadata.full_name ||
    [teacherMetadata.first_name, teacherMetadata.last_name].filter(Boolean).join(' ') ||
    teacherMetadata.name ||
    teacherMetadata.display_name ||
    (session?.user?.email ? session.user.email.split('@')[0] : '') ||
    '';
  const teacherNickname =
    teacherMetadata.preferred_name ||
    teacherMetadata.nickname ||
    teacherMetadata.first_name ||
    (teacherFullName ? teacherFullName.split(' ')[0] : '');
  const teacherRosterKey = teacherRosterId || teacherFullName || session?.user?.email || null;
  const teacherAlreadyInRoster = roster.some(
    (entry) => (entry.id || entry.full_name) === (teacherRosterKey || teacherFullName)
  );
  const pageThumbnails = useMemo(
    () =>
      (storybook?.pages || []).map((page, index) => ({
        key: page?.pageId || page?.page_id || `page-${page?.pageNumber || index + 1}`,
        pageNumber: Number(page?.pageNumber ?? index + 1) || index + 1,
        page,
        image:
          pickStorageKey(
            page?.image_url,
            page?.public_image_url,
            page?.imageUrl,
            page?.image_path,
            page?.imagePath
          ) ||
          pickRemoteUrl(page?.image_url, page?.imageUrl, page?.image_path, page?.imagePath) ||
          null,
        version: Number(page?.version) || 0,
      })),
    [storybook?.pages]
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const pushToast = useCallback((message: string, kind: string = "info") => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => [...prev, { id, message, kind }]);
    setTimeout(() => {
      if (!isMountedRef.current) return;
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 5000);
  }, []);

  const sendSettingsPatch = useCallback(
    async (patch: Record<string, unknown>) => {
      if (!storybook?.id || !patch || typeof patch !== "object") return;
      try {
        const payload = await sbUpdateSettings({
          book_id: storybook.id,
          bookId: storybook.id,
          patch,
        }) as Record<string, unknown>;

        const settings =
          payload?.settings && typeof payload.settings === "object" ? payload.settings : null;
        if (settings) {
          setStorybook((prev) => {
            if (!prev) return prev;
            const merged = { ...DEFAULT_SETTINGS, ...(prev.settings || {}), ...settings };
            return {
              ...prev,
              settings: merged,
              grade_level: settings.grade_level ?? prev.grade_level,
              teacher_notes: settings.teacher_notes ?? prev.teacher_notes,
            };
          });
        }
      } catch (error) {
        console.error("[Storybooks] settings update failed", error);
        pushToast(error instanceof Error ? error.message : "Failed to save story settings", "error");
      }
    },
    [storybook?.id, session?.access_token, pushToast]
  );

  const debouncedSettingsSave = useDebounced(sendSettingsPatch, AUTOSAVE_MS);

  const fetchStorybookData = useCallback(async () => {
    if (!bookId) {
      throw new Error("Storybook not found");
    }

    const { data, error } = await supabase
      .from("story_books")
      .select(
        "id, classroom_id, title, grade_level, teacher_notes, narrative, pages, include_illustrations, status, pdf_url, updated_at, created_at, student_profiles, settings, style_registry_id"
      )
      .eq("id", bookId)
      .maybeSingle();

    if (error && error.code !== "PGRST116") {
      throw error;
    }

    if (!data) {
      throw new Error("Storybook not found");
    }

    const mergedSettings =
      data.settings && typeof data.settings === "object" && !Array.isArray(data.settings)
        ? { ...DEFAULT_SETTINGS, ...data.settings }
        : { ...DEFAULT_SETTINGS };

    const { data: pageRows, error: pageError } = await supabase
      .from("story_pages")
      .select(
        "id, page_no, text, caption, image_url, public_image_url, layout, illustration_ready, image_updated_at, updated_at, version, last_error"
      )
      .eq("book_id", bookId)
      .order("page_no", { ascending: true });

    if (pageError && pageError.code !== "PGRST116") {
      throw pageError;
    }

    const pageRowMap = new Map<number, StoryPageRow>(
      (pageRows || [])
        .map((row): [number, StoryPageRow] | null => {
          const pageNo = Number(row?.page_no);
          return Number.isFinite(pageNo) ? [pageNo, row as StoryPageRow] : null;
        })
        .filter((entry): entry is [number, StoryPageRow] => entry !== null)
    );

    const seen = new Set<number>();

    const mappedPages = Array.isArray(data.pages)
      ? data.pages.map((pageData, index) => {
          const page = pageData as StorybookPage | null;
          const pageNumber = Number(page?.pageNumber ?? page?.page_no ?? index + 1) || index + 1;
          const row = pageRowMap.get(pageNumber) || null;
          if (row) seen.add(pageNumber);
          const pageId = row?.id || page?.pageId || page?.page_id || page?.id || null;
          const storageKey = pickStorageKey(
            row?.image_url,
            page?.image_url,
            page?.imageUrl,
            row?.image_path,
            page?.image_path,
            page?.imagePath
          );
          const legacyPath = pickStorageKey(row?.image_path, page?.image_path, page?.imagePath);
          const remoteImage = pickRemoteUrl(page?.image_url, page?.imageUrl);
          const publicKey = pickStorageKey(row?.public_image_url);
          const layout = row?.layout || page?.layout || null;
          return {
            pageId,
            pageNumber,
            text: page?.text || row?.text || "",
            caption: page?.caption || row?.caption || "",
            imageUrl: remoteImage,
            image_url: storageKey || null,
            image_path: storageKey ? null : legacyPath,
            imagePath: storageKey ? null : legacyPath,
            public_image_url: publicKey || null,
            prompt: page?.prompt || "",
            layout,
            characters: page?.characters || null,
            last_error: page?.last_error || row?.last_error || null,
            illustration_ready: Boolean(
              row?.illustration_ready ??
                page?.illustration_ready ??
                (storageKey || publicKey)
            ),
            image_updated_at: row?.image_updated_at || page?.image_updated_at || null,
            updated_at: row?.updated_at || null,
            version: Number(row?.version ?? page?.version) || 0,
          };
        })
      : [];

    const extraPages: StorybookPage[] = [];
    pageRowMap.forEach((row, pageNo) => {
      if (seen.has(pageNo)) return;
      const storageKey = pickStorageKey(row?.image_url, row?.image_path);
      const publicKey = pickStorageKey(row?.public_image_url);
      extraPages.push({
        pageId: row?.id || null,
        pageNumber: pageNo,
        text: row?.text || "",
        caption: row?.caption || "",
        imageUrl: null,
        image_url: storageKey || null,
        image_path: storageKey ? null : pickStorageKey(row?.image_path),
        imagePath: storageKey ? null : pickStorageKey(row?.image_path),
        public_image_url: publicKey || null,
        prompt: "",
        layout: row?.layout || null,
        characters: null,
        last_error: row?.last_error || null,
        illustration_ready: Boolean(
          row?.illustration_ready ?? (storageKey || publicKey)
        ),
        image_updated_at: row?.image_updated_at || null,
        updated_at: row?.updated_at || null,
        version: Number(row?.version) || 0,
      });
    });

    const combinedPages = [...mappedPages, ...extraPages].sort(
      (a, b) => Number(a.pageNumber) - Number(b.pageNumber)
    );

    return {
      ...data,
      pages: combinedPages,
      student_profiles: Array.isArray(data.student_profiles) ? (data.student_profiles as RosterEntry[]) : [],
      settings: mergedSettings,
      grade_level: mergedSettings.grade_level ?? data.grade_level ?? "",
      teacher_notes: mergedSettings.teacher_notes ?? data.teacher_notes ?? "",
    } as Storybook;
  }, [bookId]);

  const reloadStorybook = useCallback(async () => {
    try {
      setStoryError(null);
      const next = await fetchStorybookData();
      if (isMountedRef.current) {
        setStorybook(next);
        setGradeDraft(
          next?.settings?.grade_level ?? next?.grade_level ?? next?.grade ?? ""
        );
        setNotesDraft(next?.settings?.teacher_notes ?? next?.teacher_notes ?? "");
        const initialVersions = new Map<number, number>(
          (next?.pages || [])
            .map((page, index): [number, number] | null => {
              const pageNo = Number(page?.pageNumber ?? page?.page_no ?? index + 1);
              const version = Number(page?.version) || 0;
              return Number.isFinite(pageNo) ? [pageNo, version] : null;
            })
            .filter((entry): entry is [number, number] => entry !== null)
        );
        pageVersionRef.current = initialVersions;
      }
      await refresh();
      return next;
    } catch (error) {
      if (isMountedRef.current) {
        setStoryError(error instanceof Error ? error : new Error(String(error)));
      }
      throw error;
    }
  }, [fetchStorybookData, refresh]);

  useEffect(() => {
    if (!bookId || !enabled) return;

    let active = true;
    (async () => {
      setLoading(true);
      setStoryError(null);
      try {
        const next = await fetchStorybookData();
        if (active) {
          setStorybook(next);
          setGradeDraft(
            next?.settings?.grade_level ?? next?.grade_level ?? next?.grade ?? ""
          );
          setNotesDraft(next?.settings?.teacher_notes ?? next?.teacher_notes ?? "");
          const initialVersions = new Map<number, number>(
            (next?.pages || [])
              .map((page, index): [number, number] | null => {
                const pageNo = Number(page?.pageNumber ?? page?.page_no ?? index + 1);
                const version = Number(page?.version) || 0;
                return Number.isFinite(pageNo) ? [pageNo, version] : null;
              })
              .filter((entry): entry is [number, number] => entry !== null)
          );
          pageVersionRef.current = initialVersions;
        }
      } catch (error) {
        if (active) {
          setStoryError(error instanceof Error ? error : new Error(String(error)));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [enabled, bookId, fetchStorybookData]);

  useEffect(() => {
    if (!storybook?.classroom_id) return;
    if (!session?.access_token) return;

    let cancelled = false;
    (async () => {
      setStudentsLoading(true);
      setStudentsError(null);
      try {
        const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
        const raw = await jsonFetch(`${CORE_API}/api/v1/students`) as { data?: TeacherStudentsResponse } & TeacherStudentsResponse;
        const payload = (raw?.data ?? raw) as TeacherStudentsResponse;
        const roster = Array.isArray(payload?.students) ? payload.students : [];
        if (!cancelled) {
          setStudents(
            roster.map((student: Student) => {
              const profile = student?.profile || null;
              const traits = Array.isArray(profile?.traits) ? profile.traits : [];
              const descriptors = Array.isArray(profile?.descriptors) ? profile.descriptors : [];
              const notes = Array.isArray(profile?.notes) ? profile.notes : [];
              return {
                ...student,
                full_name: student.full_name ?? student.display_name ?? "Student",
                traits,
                descriptors,
                notes,
                profile,
              };
            }),
          );
        }
      } catch (error) {
        if (!cancelled) {
          setStudentsError(error instanceof Error ? error.message : String(error));
          setStudents([]);
        }
      } finally {
        if (!cancelled) {
          setStudentsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session?.access_token, storybook?.classroom_id]);

  useEffect(() => {
    if (!storybook) return;
    const nextGrade =
      storybook?.settings?.grade_level ?? storybook?.grade_level ?? storybook?.grade ?? "";
    const nextNotes =
      storybook?.settings?.teacher_notes ?? storybook?.teacher_notes ?? "";

    if (nextGrade !== gradeDraft) {
      setGradeDraft(nextGrade);
    }
    if (nextNotes !== notesDraft) {
      setNotesDraft(nextNotes);
    }
  }, [
    storybook?.id,
    storybook?.settings?.grade_level,
    storybook?.settings?.teacher_notes,
    storybook?.grade_level,
    storybook?.teacher_notes,
    storybook?.grade,
    gradeDraft,
    notesDraft,
  ]);

  const handleFieldChange = (field: keyof Storybook, value: string) => {
    setStorybook((prev) =>
      prev ? { ...prev, [field]: value, updated_at: new Date().toISOString() } : prev
    );
  };

  const handleGradeLevelChange = useCallback(
    (value: string) => {
      const nextValue = value ?? "";
      setGradeDraft(nextValue);
      setStorybook((prev) => {
        if (!prev) return prev;
        const merged = { ...DEFAULT_SETTINGS, ...(prev.settings || {}) };
        merged.grade_level = nextValue;
        return {
          ...prev,
          grade_level: nextValue,
          settings: merged,
          updated_at: new Date().toISOString(),
        };
      });
      debouncedSettingsSave({ grade_level: nextValue });
    },
    [debouncedSettingsSave]
  );

  const handleNotesChange = useCallback(
    (value: string) => {
      const nextValue = value ?? "";
      setNotesDraft(nextValue);
      setStorybook((prev) => {
        if (!prev) return prev;
        const merged = { ...DEFAULT_SETTINGS, ...(prev.settings || {}) };
        merged.teacher_notes = nextValue;
        return {
          ...prev,
          teacher_notes: nextValue,
          settings: merged,
          updated_at: new Date().toISOString(),
        };
      });
      debouncedSettingsSave({ teacher_notes: nextValue });
    },
    [debouncedSettingsSave]
  );

  const handlePageChange = (pageNumber: number, field: string, value: string) => {
    setStorybook((prev) => {
      if (!prev) return prev;
      const nextPages = prev.pages.map((page) =>
        Number(page.pageNumber) === Number(pageNumber) ? { ...page, [field]: value } : page
      );
      return { ...prev, pages: nextPages, updated_at: new Date().toISOString() };
    });
  };

  const handleSettingChange = (key: string, value: string | number) => {
    setStorybook((prev) => {
      if (!prev) return prev;
      const base =
        prev.settings && typeof prev.settings === "object" && !Array.isArray(prev.settings)
          ? prev.settings
          : {};
      const nextSettings = { ...DEFAULT_SETTINGS, ...base, [key]: value };
      const includeIllustrations =
        (key === "illustrationMode" ? value : nextSettings.illustrationMode) !== "none";
      return {
        ...prev,
        settings: nextSettings,
        include_illustrations: includeIllustrations,
        updated_at: new Date().toISOString(),
      };
    });
  };

  const handleToggleStudent = (student: Student) => {
    if (!student) return;
    const id = student.id || student.student_id || student.participant_id || null;
    const fullName = student.full_name || student.name || student.display_name || "";
    setStorybook((prev) => {
      if (!prev) return prev;
      const current = Array.isArray(prev.student_profiles) ? prev.student_profiles : [];
      const exists = current.some((entry) => (entry.id || entry.full_name) === (id || fullName));
      const next = exists
        ? current.filter((entry) => (entry.id || entry.full_name) !== (id || fullName))
        : [
            ...current,
            {
              id,
              full_name: fullName,
              grade: student.grade || "",
              nickname: student.nickname || "",
              role: student.role || "",
              traits: Array.isArray(student.traits)
                ? student.traits
                : student.traits
                ? String(student.traits)
                : "",
            },
          ];
      return { ...prev, student_profiles: next, updated_at: new Date().toISOString() };
    });
  };

  const handleRosterFieldChange = (studentId: string, field: string, value: string) => {
    setStorybook((prev) => {
      if (!prev) return prev;
      const current = Array.isArray(prev.student_profiles) ? prev.student_profiles : [];
      const next = current.map((entry) => {
        const key = entry.id || entry.full_name;
        if (key !== studentId) return entry;
        return { ...entry, [field]: value };
      });
      return { ...prev, student_profiles: next, updated_at: new Date().toISOString() };
    });
  };

  const handleRemoveRosterEntry = (studentId: string | null | undefined) => {
    let removed = false;
    setStorybook((prev) => {
      if (!prev) return prev;
      const current = Array.isArray(prev.student_profiles) ? prev.student_profiles : [];
      const next = current.filter((entry) => {
        const key = entry.id || entry.full_name;
        const keep = key !== studentId;
        if (!keep) removed = true;
        return keep;
      });
      return { ...prev, student_profiles: next, updated_at: new Date().toISOString() };
    });
    if (removed) {
      pushToast("Removed from story cast.", "info");
    }
  };

  const handleAddTeacherProfile = () => {
    if (!teacherFullName) {
      pushToast("Add your name to your profile to include yourself in the story.", "warn");
      return;
    }

    if (teacherAlreadyInRoster) {
      pushToast("You're already part of this storybook.", "info");
      return;
    }

    let added = false;
    setStorybook((prev) => {
      if (!prev) return prev;
      const current = Array.isArray(prev.student_profiles) ? prev.student_profiles : [];
      const next = [
        ...current,
        {
          id: teacherRosterId,
          full_name: teacherFullName,
          nickname: teacherNickname || "",
          role: "Teacher narrator",
          traits: "",
          type: "teacher",
        },
      ];
      added = true;
      return { ...prev, student_profiles: next, updated_at: new Date().toISOString() };
    });

    if (added) {
      pushToast("Added you to the cast. Update your role or traits below.", "success");
    }
  };

  const handleClearRoster = () => {
    setStorybook((prev) => (prev ? { ...prev, student_profiles: [], updated_at: new Date().toISOString() } : prev));
  };

  const persistStorybook = async (patch: Record<string, unknown> = {}) => {
    if (!storybook) return;
    setSaveLoading(true);
    setStoryError(null);
    try {
      const sanitizedPages = Array.isArray(storybook.pages)
        ? storybook.pages.map((page, index) => {
            const pageNumber = Number(page?.pageNumber ?? index + 1) || index + 1;
            const base: SanitizedPageBase = {
              pageId: page?.pageId || page?.page_id || page?.id || null,
              pageNumber,
              text: typeof page?.text === "string" ? page.text : "",
              caption: typeof page?.caption === "string" ? page.caption : "",
              prompt: typeof page?.prompt === "string" ? page.prompt : "",
              imageUrl: pickRemoteUrl(page?.image_url, page?.imageUrl),
              image_url: pickStorageKey(
                page?.image_url,
                page?.public_image_url,
                page?.imageUrl,
                page?.image_path,
                page?.imagePath
              ),
            };
            if (!base.image_url) {
              const legacyPath = pickStorageKey(page?.image_path, page?.imagePath);
              if (legacyPath) base.image_path = legacyPath;
            }
            const publicKey = pickStorageKey(page?.public_image_url);
            if (publicKey) base.public_image_url = publicKey;
            if (page?.layout) base.layout = page.layout;
            if (page?.characters) base.characters = page.characters;
            if (page?.last_error) base.last_error = page.last_error;
            return base;
          })
        : [];

      const updatePayload: Record<string, unknown> = {
        title: storybook.title,
        grade_level: gradeDraft || null,
        teacher_notes: notesDraft || null,
        narrative: storybook.narrative || null,
        pages: sanitizedPages,
        include_illustrations: (settings.illustrationMode || "full") !== "none",
        student_profiles: roster,
        settings,
        ...patch,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from("story_books")
        .update(updatePayload)
        .eq("id", storybook.id);
      if (error) throw error;
      refresh();
    } catch (err) {
      console.error("[Storybooks] save error", err);
      setStoryError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setSaveLoading(false);
    }
  };

  const callFunction = async <T = unknown>(endpoint: string, body: Record<string, unknown>): Promise<T> => {
    return sbFetch<T>(endpoint, {
      method: "POST",
      body,
    });
  };

  const handleGenerateText = async () => {
    if (!storybook) return;
    if (!bookId) {
      setStoryError(new Error("Missing book id"));
      return;
    }
    try {
      setActionLoading(true);
      await callFunction("/api/storybooks/generate-text", {
        book_id: bookId,
        teacher_notes: storybook.teacher_notes || "",
        page_count: Number(settings.pageCount) || DEFAULT_SETTINGS.pageCount,
        student_profiles: roster,
      });
      await reloadStorybook();
    } catch (err) {
      console.error("[Storybooks] regenerate text error", err);
      setStoryError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setActionLoading(false);
    }
  };

  const mapPagesForApi = useCallback((pages: StorybookPage[]): ApiPageEntry[] => {
    if (!Array.isArray(pages)) return [];
    return pages
      .map((page, index): ApiPageEntry | null => {
        const pageNumber = Number(page?.pageNumber ?? page?.page_no ?? index + 1);
        if (!Number.isFinite(pageNumber) || pageNumber <= 0) return null;

        const text = typeof page?.text === "string" ? page.text : "";
        const prompt = typeof page?.prompt === "string" ? page.prompt : "";

        const entry: ApiPageEntry = {
          page_no: pageNumber,
          text,
        };

        if (page?.pageId || page?.page_id) {
          entry.page_id = page.pageId || page.page_id;
        }

        if (prompt) entry.prompt = prompt;
        if (page?.layout) entry.layout = page.layout;
        if (page?.characters) entry.characters = page.characters;
        const storageKey = pickStorageKey(
          page?.image_url,
          page?.imageUrl,
          page?.image_path,
          page?.imagePath
        );
        if (storageKey) {
          entry.image_url = storageKey;
        } else {
          const legacyPath = pickStorageKey(page?.image_path, page?.imagePath);
          if (legacyPath) entry.image_path = legacyPath;
        }

        return entry;
      })
      .filter((entry): entry is ApiPageEntry => entry !== null);
  }, []);

  useEffect(() => {
    if (!bookId) {
      setJobId(null);
      setJob(null);
      setJobSummary(null);
      setShowProgress(false);
      jobActiveRef.current = false;
      return;
    }

    if (typeof window === 'undefined' || !storageKey) return;

    const storedJobId = localStorage.getItem(storageKey);
    setJobId(storedJobId || null);
  }, [bookId, storageKey]);

  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    if (jobId) {
      localStorage.setItem(storageKey, jobId);
    } else {
      localStorage.removeItem(storageKey);
    }
  }, [jobId, storageKey]);

  useEffect(() => {
    if (!bookId || jobId) return;

    let cancelled = false;

    const fetchLatest = async () => {
      try {
        const result = await getJobStatus({ book_id: bookId, latest: true });
        if (cancelled) return;

        const data = result?.data?.job ?? (result as any)?.job ?? null;
        if (!data) {
          setJob(null);
          return;
        }

        const latestId = (data.id || '').toString().trim();
        if (latestId) {
          setJobId((prev) => prev || latestId);
        }
        setJob(data as RenderJob);
        const summary = buildJobSummary(data as RenderJob);
        if (summary) {
          setJobSummary(summary);
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('[storybooks] latest job lookup failed', error);
        }
      }
    };

    fetchLatest();

    return () => {
      cancelled = true;
    };
  }, [bookId, jobId, buildJobSummary]);

  useEffect(() => {
    if (!jobId) return;

    let cancelled = false;
    const abortTimer = setTimeout(() => {
      if (cancelled) return;
      setJob((prev) => {
        if (!prev) return prev;
        const statusValue = (prev.status || '').toString().toLowerCase();
        if (TERMINAL_STATUSES.has(statusValue)) return prev;
        return {
          ...prev,
          status: 'error',
          last_error: prev.last_error || 'Job timed out after 15 minutes.',
          terminal: true,
        };
      });
      setJobId(null);
      setShowProgress(false);
    }, 15 * 60 * 1000);

    const tick = async () => {
      try {
        const result = await getJobStatus({ job_id: jobId });
        if (cancelled) return;

        const data = result.data.job;
        if (!data) {
          setJob(null);
          setJobId(null);
          setShowProgress(false);
          setJobSummary(null);
          return;
        }

        setJob(data as RenderJob);
        const summary = buildJobSummary(data as RenderJob);
        if (summary) {
          clearTimeout(abortTimer);
          setJobSummary(summary);
          setJobId(null);
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('[job-status] poll error', error);
        }
      }
    };

    const intervalId = setInterval(tick, 1500);
    tick();

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      clearTimeout(abortTimer);
    };
  }, [jobId, buildJobSummary]);

  /**
   * Generate narrative text after illustrations complete (V2 flow step 2)
   */
  const generateNarrativeAfterIllustrations = useCallback(async () => {
    if (!storybook?.id) return;

    try {
      console.log("[Storybooks v2] Generating narrative text to match illustrations");
      pushToast("Writing story text to match your illustrations...", "info");

      const result = await v2GenerateNarrative({
        book_id: storybook.id,
        teacher_notes: storybook.teacher_notes || undefined,
      });

      console.log("[Storybooks v2] Narrative generated successfully");
      pushToast("Story text generated! Your storybook is ready.", "success");

      // Reload to show the new narrative
      await reloadStorybook();
      setPreviewIndex(0);
      setPreviewOpen(true);

    } catch (error) {
      console.error("[Storybooks v2] narrative generation error", error);
      pushToast("Illustrations done but text generation failed. You can retry.", "warning");
    }
  }, [storybook?.id, storybook?.teacher_notes, pushToast, reloadStorybook]);

  useEffect(() => {
    const prevStatus = prevJobStatusRef.current;
    const normalized = jobStatus || 'idle';

    if (normalized === 'queued' || normalized === 'running') {
      jobActiveRef.current = true;
      setJobSummary(null);
    }

    const finished =
      jobActiveRef.current && TERMINAL_STATUSES.has(normalized) && prevStatus !== normalized;

    let closeTimer = null;

    if (finished) {
      jobActiveRef.current = false;

      const summaryPayload = {
        ...job,
        status: normalized,
        completed_pages: jobCompletedPages,
        total_pages: jobTotalPages,
        failed_pages: jobFailedPages,
        percent: jobPercentDisplay,
        notes: jobNotes,
        last_error: jobLastError,
      };
      const summary = buildJobSummary(summaryPayload);
      if (summary) {
        setJobSummary(summary);
      }

      void (async () => {
        try {
          await reloadStorybook();
          setRefreshTick((value) => value + 1);
        } catch (error) {
          console.error('[Storybooks] reload after job', error);
        }

        if (!isMountedRef.current) return;

        if (SUCCESS_STATUSES.has(normalized)) {
          // V2 Flow: Generate narrative text now that illustrations are done
          pushToast('Illustrations complete! Generating story text...', 'success');
          try {
            await generateNarrativeAfterIllustrations();
          } catch (err) {
            console.error('[Storybooks v2] narrative generation failed', err);
            pushToast('Illustrations done. Click to generate story text.', 'info');
            setPreviewIndex(0);
            setPreviewOpen(true);
          }
        } else if (PARTIAL_STATUSES.has(normalized)) {
          pushToast('Illustrations finished with a few issues. Review highlighted pages.', 'warn');
          setShowProgress(true);
        } else if (FAILED_STATUSES.has(normalized)) {
          const message = jobLastError || 'Illustration job failed. Please try again.';
          pushToast(message, 'error');
        } else if (CANCELLED_STATUSES.has(normalized)) {
          pushToast('Illustration job was cancelled.', 'warn');
        }

        setJobId(null);
      })();

      closeTimer = setTimeout(() => {
        setShowProgress(false);
      }, 800);
    }

    if (!['queued', 'running'].includes(normalized)) {
      jobActiveRef.current = false;
    }

    prevJobStatusRef.current = normalized;

    return () => {
      if (closeTimer) clearTimeout(closeTimer);
    };
  }, [
    job,
    jobStatus,
    jobCompletedPages,
    jobTotalPages,
    jobFailedPages,
    jobNotes,
    jobPercentDisplay,
    jobLastError,
    reloadStorybook,
    pushToast,
    setRefreshTick,
    buildJobSummary,
    generateNarrativeAfterIllustrations,
  ]);



  /**
   * V2 One-Button Flow (Visual-First Workflow)
   *
   * Flow: Characters → Illustrations → Text
   * 1. Build characters from student profiles
   * 2. Generate arc-based scene descriptions
   * 3. Queue illustration job (worker renders images)
   * 4. After illustrations complete: Generate narrative text that matches
   */
  const oneButtonFlow = useCallback(async ({ force = false } = {}) => {
    if (!storybook) return;
    const currentBookId = storybook.id?.trim();
    const classroomId = storybook.classroom_id || classroom?.id;
    const desiredPageCount = Number(settings.pageCount) || DEFAULT_SETTINGS.pageCount;
    const illustrationMode = settings.illustrationMode || "full";

    if (!currentBookId) {
      setStoryError(new Error("Missing book id"));
      return;
    }
    if (!classroomId) {
      setStoryError(new Error("Missing classroom id"));
      return;
    }

    if (illustrationMode === "none") {
      // No illustrations mode - just generate text the old way
      pushToast("Text-only mode not yet supported in v2 flow", "info");
      return;
    }

    setActionLoading(true);
    setStoryError(null);
    setJobSummary(null);

    try {
      console.log("[Storybooks v2] Starting visual-first flow");
      pushToast("Building characters and preparing illustrations...", "info");

      // Step 1: Initialize V2 flow (builds characters + queues illustrations)
      const initResult = await v2Initialize({
        book_id: currentBookId,
        classroom_id: classroomId,
        page_count: desiredPageCount,
        style_registry_id: storybook.style_registry_id || undefined,
        student_profiles: roster,
        teacher_notes: storybook.teacher_notes || "",
      });

      const { job_id, page_count, characters, scenes } = initResult.data;
      console.log(`[Storybooks v2] Created job ${job_id} with ${characters.length} characters, ${page_count} pages`);

      if (!job_id) throw new Error("Missing job id");

      // Set up job tracking
      setJob({
        id: job_id,
        status: 'queued',
        completed_pages: 0,
        total_pages: page_count,
        failed_pages: 0,
        progress_pct: 0,
        last_error: null,
        percent: 0,
        terminal: false,
      });
      setJobId(job_id);
      setShowProgress(true);
      jobActiveRef.current = true;

      pushToast(`Rendering ${page_count} illustrations with ${characters.length} characters...`, 'info');

      // The job polling will handle status updates
      // When job completes, we'll generate narrative text
      // This is handled by the job completion callback (see below)

    } catch (error) {
      console.error("[Storybooks v2] flow error", error);
      setStoryError(error instanceof Error ? error : new Error(String(error)));
      pushToast(error instanceof Error ? error.message : "We couldn't complete that request.", "error");
    } finally {
      if (isMountedRef.current) {
        setActionLoading(false);
      }
    }
  }, [
    storybook,
    classroom?.id,
    pushToast,
    settings,
    roster,
  ]);

  const handleRenderPdf = async () => {
    if (!storybook) return;
    if (!bookId) {
      setStoryError(new Error("Missing book id"));
      return;
    }
    try {
      setActionLoading(true);
      const payload = await callFunction<RenderPdfResponse>("/api/storybooks/render-pdf", {
        book_id: bookId,
      });
      const nextUrl = payload?.url || payload?.signedUrl || payload?.pdf_url || payload?.pdfUrl || null;
      setStorybook((prev) =>
        prev ? { ...prev, pdf_url: nextUrl || prev.pdf_url, updated_at: new Date().toISOString() } : prev
      );
      refresh();
    } catch (err) {
      console.error("[Storybooks] render pdf error", err);
      setStoryError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitDraft = async () => {
    await persistStorybook({ status: "submitted" });
    setStorybook((prev) => (prev ? { ...prev, status: "submitted" } : prev));
  };

  const handleRenderPage = async (pageNumber: number, { force = false }: { force?: boolean } = {}) => {
    if (!storybook) return;
    if (!bookId) {
      setStoryError(new Error("Missing book id"));
      return;
    }
    const classroomId = storybook.classroom_id || classroom?.id;
    if (!classroomId) {
      setStoryError(new Error("Missing classroom id"));
      return;
    }
    const page = (storybook.pages || []).find((entry) => Number(entry.pageNumber) === Number(pageNumber));
    if (!page) {
      pushToast("Select a valid page to render.", "error");
      return;
    }
    if (!page.text || !page.text.trim()) {
      pushToast("Add story text to this page before rendering an illustration.", "warn");
      return;
    }

    setPageBusy((prev) => ({ ...prev, [pageNumber]: true }));
    try {
      const payload = await callFunction<GenerateIllustrationResponse>("/api/storybooks/generate-illustration", {
        book_id: bookId,
        classroom_id: classroomId,
        page_no: pageNumber,
        page_id: page.pageId || page.page_id || null,
        text: page.text,
        force,
      });

      const rendered = (payload?.page || payload) as GenerateIllustrationResponse;
      const renderedStorageKey = pickStorageKey(
        rendered?.image_url,
        rendered?.imageUrl,
        rendered?.image_path
      );
      const renderedRemote = pickRemoteUrl(rendered?.image_url, rendered?.imageUrl);
      if (renderedStorageKey || renderedRemote) {
        setStorybook((prev) => {
          if (!prev) return prev;
          const nextPages = prev.pages.map((entry) =>
            Number(entry.pageNumber) === Number(pageNumber)
              ? {
                  ...entry,
                  image_url: renderedStorageKey || entry.image_url || null,
                  imageUrl: renderedRemote || entry.imageUrl || null,
                  image_path:
                    renderedStorageKey
                      ? null
                      : rendered.image_path || entry.image_path || entry.imagePath || null,
                  imagePath:
                    renderedStorageKey
                      ? null
                      : rendered.image_path || entry.image_path || entry.imagePath || null,
                  public_image_url: entry.public_image_url || null,
                  illustration_ready: true,
                  version: Number(rendered.version) || Number(entry.version) || 0,
                  image_updated_at:
                    rendered.image_updated_at || entry.image_updated_at || new Date().toISOString(),
                  pageId: rendered.page_id || rendered.pageId || entry.pageId || entry.page_id || null,
                }
              : entry
          );
          return { ...prev, pages: nextPages, updated_at: new Date().toISOString() };
        });
        const pageNo = Number(pageNumber);
        if (Number.isFinite(pageNo)) {
          const nextVersion = Number(rendered?.version) || 0;
          const versionsMap = new Map(pageVersionRef.current || []);
          versionsMap.set(pageNo, nextVersion);
          pageVersionRef.current = versionsMap;
        }
        setRefreshTick((value) => value + 1);
        setPreviewIndex(Math.max(0, Number(pageNumber) - 1));
        setPreviewOpen(true);
        pushToast(`Rendered illustration for page ${pageNumber}.`, "success");
      }
      await reloadStorybook();
    } catch (error) {
      console.error("[Storybooks] single page render error", error);
      pushToast(error instanceof Error ? error.message : "Unable to render this page.", "error");
    } finally {
      setPageBusy((prev) => ({ ...prev, [pageNumber]: false }));
    }
  };

  const handlePreviewPage = (pageNumber: number) => {
    const index = Math.max(
      0,
      (storybook?.pages || []).findIndex((entry) => Number(entry.pageNumber) === Number(pageNumber))
    );
    setPreviewIndex(index);
    setPreviewOpen(true);
  };

  const handlePublish = async () => {
    if (!storybook) return;
    if (!bookId) {
      setStoryError(new Error("Missing book id"));
      return;
    }
    try {
      setActionLoading(true);
      const payload = await callFunction<PublishStorybookResponse>("/api/storybooks/publish-storybook", {
        book_id: bookId,
        storybookId: bookId,
        consent: consentState,
      });
      await reloadStorybook();
      setStorybook((prev) =>
        prev
          ? {
              ...prev,
              status: payload?.status || "published",
              published_at: payload?.publishedAt || new Date().toISOString(),
              updated_at: new Date().toISOString(),
            }
          : prev
      );
      setConsentOpen(false);
      refresh();
    } catch (err) {
      console.error("[Storybooks] publish error", err);
      setStoryError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setActionLoading(false);
    }
  };

  const statusBadge = useMemo(() => {
    if (!storybook) return null;
    const map: Record<string, string> = {
      draft: "bg-sand text-cocoa",
      submitted: "bg-blue-100 text-blue-800",
      approved: "bg-emerald-100 text-emerald-800",
      published: "bg-brick text-white",
    };
    const status = storybook.status || "draft";
    const label = status.charAt(0).toUpperCase() + status.slice(1);
    return (
      <span
        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
          map[status] || "bg-sand"
        }`}
      >
        {label}
      </span>
    );
  }, [storybook]);

  const storybookHasImages = useMemo(() => {
    if (!storybook) return false;
    return (storybook.pages || []).some(
      (page) =>
        page?.illustration_ready ||
        pickStorageKey(
          page?.image_url,
          page?.public_image_url,
          page?.imageUrl,
          page?.image_path,
          page?.imagePath
        ) ||
        pickRemoteUrl(page?.image_url, page?.imageUrl, page?.image_path, page?.imagePath)
    );
  }, [storybook]);

  if (!enabled) {
    return <p className="text-sm text-cocoa/70">Storybooks are not enabled.</p>;
  }
  if (contextLoading || loading) {
    return <p className="text-sm text-cocoa/70">Loading storybook…</p>;
  }
  if (contextError) {
    return (
      <p className="rounded-2xl bg-rose-100 p-4 text-sm text-rose-700">
        We couldn’t load Storybooks context. Please refresh and try again later.
      </p>
    );
  }
  if (!featureEnabled) {
    return (
      <p className="rounded-2xl bg-shell p-4 text-sm text-cocoa/70">
        Storybooks are disabled for this classroom. Contact your Project Tower team to opt in.
      </p>
    );
  }
  if (storyError) {
    return (
      <div className="space-y-4">
        <p className="rounded-2xl bg-rose-100 p-4 text-sm text-rose-700">
          {storyError.message || "Unable to load storybook."}
        </p>
        <button type="button" className="btn-secondary" onClick={() => navigate("..")}>
          Back to Storybooks
        </button>
      </div>
    );
  }
  if (!storybook) {
    return (
      <div className="space-y-4">
        <p className="rounded-2xl bg-shell p-4 text-sm text-cocoa/70">
          Storybook not found or still loading.
        </p>
        <button type="button" className="btn-secondary" onClick={() => navigate("..")}>
          Back to Storybooks
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <button
          type="button"
          className="text-sm text-cocoa/70 underline-offset-4 hover:underline"
          onClick={() => navigate("..")}
        >
          ← Back to Storybooks
        </button>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-semibold text-cocoa">{storybook.title}</h1>
          {statusBadge}
        </div>
        <p className="text-sm text-cocoa/60">
          Last updated{" "}
          {new Date(
            storybook.updated_at || storybook.created_at || Date.now()
          ).toLocaleString()}
        </p>
      </header>

      {toasts.length > 0 && (
        <div className="space-y-2">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`rounded-2xl px-4 py-3 text-sm ${
                toast.kind === "warn"
                  ? "bg-amber-50 text-amber-800"
                  : toast.kind === "error"
                  ? "bg-rose-50 text-rose-800"
                  : "bg-sky-50 text-sky-900"
              }`}
            >
              {toast.message}
            </div>
          ))}
        </div>
      )}

      {jobSummaryDetails && (
        <div
          className={`flex flex-col gap-3 rounded-2xl border px-4 py-4 shadow-soft ${
            summaryToneClasses[jobSummaryDetails.tone] || summaryToneClasses.info
          }`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1 text-sm">
              <p className="text-base font-semibold">{jobSummaryDetails.title}</p>
              {jobSummaryDetails.description && (
                <p className="text-xs/relaxed opacity-80">{jobSummaryDetails.description}</p>
              )}
              <p className="text-xs font-medium opacity-80">
                {jobSummaryDetails.data.percent}% • {jobSummaryDetails.data.completed}/
                {jobSummaryDetails.data.total} pages ready
                {jobSummaryDetails.data.failed > 0
                  ? ` • ${jobSummaryDetails.data.failed} issue${
                      jobSummaryDetails.data.failed === 1 ? '' : 's'
                    }`
                  : ''}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setPreviewIndex(0);
                  setPreviewOpen(true);
                }}
              >
                View pages
              </button>
              <button
                type="button"
                className="rounded-full border border-current px-3 py-1 text-xs font-medium opacity-80 transition hover:opacity-100"
                onClick={() => setJobSummary(null)}
              >
                Dismiss
              </button>
            </div>
          </div>
          {jobSummaryDetails.data.notes?.length ? (
            <div className="rounded-2xl bg-white/70 p-3 text-xs text-cocoa/80">
              <p className="font-semibold text-cocoa">Notes</p>
              <ul className="mt-1 space-y-1">
                {jobSummaryDetails.data.notes.slice(0, 5).map((note, idx) => {
                  const label = typeof note === 'string' ? note : note?.error || note?.message;
                  const page = note?.page_no || note?.page || null;
                  return (
                    <li key={idx} className="flex items-start gap-2">
                      {page ? <span className="font-semibold text-cocoa">p.{page}</span> : null}
                      <span className="flex-1 text-cocoa/70">{label || 'Issue recorded'}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      )}

      {jobIsActive && !showProgress && (
        <div className="flex w-full flex-wrap items-center justify-between gap-3 rounded-2xl bg-shell px-4 py-3 text-sm text-cocoa">
          <div className="space-y-1">
            <p className="font-semibold text-cocoa">Illustrations are rendering…</p>
            <p className="text-xs text-cocoa/70">
              {jobCompletedPages}/{jobTotalPages} pages • {jobPercentDisplay}%
              {jobFailedPages > 0 ? ` • ${jobFailedPages} issue${jobFailedPages === 1 ? '' : 's'}` : ''}
            </p>
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowProgress(true)}
          >
            View progress
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button className="btn" onClick={() => oneButtonFlow()} disabled={actionLoading}>
          {actionLoading
            ? "Working…"
            : storybookHasImages
            ? "Re-render all (honor cache)"
            : "Create my storybook"}
        </button>
        <button
          type="button"
          className="text-sm underline text-cocoa/70"
          onClick={() => setShowCustomize((prev) => !prev)}
        >
          {showCustomize ? "Hide options" : "Customize"}
        </button>
      </div>

      {jobIsActive && (
        <div className="inline-flex items-center gap-2 rounded-xl bg-shell px-3 py-2 text-xs text-cocoa">
          <span>{jobStatus === 'queued' ? 'Queued' : 'Rendering'}</span>
          <span>• {jobPercentDisplay}%</span>
          {jobFailedPages > 0 && <span>• {jobFailedPages} issue{jobFailedPages === 1 ? '' : 's'}</span>}
        </div>
      )}

      {showCustomize && (
        <div className="mt-4 space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <details open className="rounded-3xl border border-sand bg-white shadow-soft">
              <summary className="flex cursor-pointer items-center justify-between gap-2 px-5 py-4 text-sm font-semibold text-cocoa [&::-webkit-details-marker]:hidden">
                <span>Story setup</span>
                <span className="text-xs uppercase tracking-wide text-cocoa/40">Toggle</span>
              </summary>
              <div className="space-y-5 border-t border-sand/60 px-5 py-5">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-cocoa" htmlFor="storybook-title-field">
                    Title
                  </label>
                  <input
                    id="storybook-title-field"
                    type="text"
                    className="input"
                    value={storybook.title}
                    onChange={(event) => handleFieldChange("title", event.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-cocoa" htmlFor="storybook-grade-field">
                    Grade level
                  </label>
                  <input
                    id="storybook-grade-field"
                    type="text"
                    className="input"
                    value={gradeDraft}
                    onChange={(event) => handleGradeLevelChange(event.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-cocoa" htmlFor="storybook-notes-field">
                    Teacher notes
                  </label>
                  <textarea
                    id="storybook-notes-field"
                    className="input min-h-[120px]"
                    value={notesDraft}
                    onChange={(event) => handleNotesChange(event.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-cocoa" htmlFor="storybook-narrative-field">
                    Narrative
                  </label>
                  <textarea
                    id="storybook-narrative-field"
                    className="input min-h-[220px]"
                    value={storybook.narrative || ""}
                    onChange={(event) => handleFieldChange("narrative", event.target.value)}
                  />
                </div>

                <div className="space-y-3">
                  <h2 className="text-lg font-semibold text-cocoa">Story settings</h2>
                  <div className="space-y-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-cocoa/60">Illustrations</p>
                    <div className="flex flex-wrap gap-2">
                      {ILLUSTRATION_OPTIONS.map((option) => {
                        const active = settings.illustrationMode === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            className={`rounded-full px-4 py-2 text-sm transition ${
                              active
                                ? "bg-brick text-white shadow-soft"
                                : "bg-shell text-cocoa/70 hover:bg-sand"
                            }`}
                            onClick={() => handleSettingChange("illustrationMode", option.value)}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-cocoa/60">Page count</p>
                    <div className="flex flex-wrap gap-2">
                      {PAGE_COUNT_OPTIONS.map((count) => {
                        const active = Number(settings.pageCount) === Number(count);
                        return (
                          <button
                            key={count}
                            type="button"
                            className={`rounded-full px-4 py-2 text-sm transition ${
                              active
                                ? "bg-cocoa text-white shadow-soft"
                                : "bg-shell text-cocoa/70 hover:bg-sand"
                            }`}
                            onClick={() => handleSettingChange("pageCount", Number(count))}
                          >
                            {count} pages
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-cocoa/60">
                      Choose how many spreads to generate before illustrating.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-cocoa/60">Render mode</p>
                    <div className="flex flex-wrap gap-2">
                      {RENDER_MODES.map((mode) => {
                        const active = settings.renderMode === mode.value;
                        return (
                          <button
                            key={mode.value}
                            type="button"
                            className={`rounded-full px-4 py-2 text-sm transition ${
                              active
                                ? "bg-emerald-600 text-white shadow-soft"
                                : "bg-shell text-cocoa/70 hover:bg-sand"
                            }`}
                            onClick={() => handleSettingChange("renderMode", mode.value)}
                          >
                            {mode.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-cocoa/60">
                      Pick “Page by page” to iterate on individual spreads before running the one-button flow.
                    </p>
                  </div>
                </div>
              </div>
            </details>

            <details open className="rounded-3xl border border-sand bg-white shadow-soft">
              <summary className="flex cursor-pointer items-center justify-between gap-2 px-5 py-4 text-sm font-semibold text-cocoa [&::-webkit-details-marker]:hidden">
                <span>Class roster</span>
                <span className="text-xs uppercase tracking-wide text-cocoa/40">Toggle</span>
              </summary>
              <div className="space-y-4 border-t border-sand/60 px-5 py-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-1">
                    <h2 className="text-lg font-semibold text-cocoa">Cast members</h2>
                    <p className="text-sm text-cocoa/70">
                      Select students to feature and add yourself to guide the narrative.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="btn-secondary whitespace-nowrap"
                      onClick={handleAddTeacherProfile}
                      disabled={teacherAlreadyInRoster}
                    >
                      {teacherAlreadyInRoster ? "You’re in the story" : "Add myself"}
                    </button>
                    {roster.length > 0 && (
                      <button
                        type="button"
                        className="text-xs text-rose-600 underline-offset-4 hover:underline"
                        onClick={handleClearRoster}
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                </div>

                <div className="max-h-56 overflow-auto rounded-3xl border border-sand bg-shell/40">
                  {studentsLoading ? (
                    <p className="p-3 text-sm text-cocoa/60">Loading roster…</p>
                  ) : studentsError ? (
                    <p className="p-3 text-sm text-rose-700">{studentsError}</p>
                  ) : students.length ? (
                    students.map((student) => {
                      const key = student.id || student.student_id || student.full_name || student.name;
                      const selected = roster.some((entry) => (entry.id || entry.full_name) === (student.id || student.full_name));
                      return (
                        <label
                          key={key}
                          className="flex items-center justify-between gap-3 border-b border-sand/60 px-4 py-3 text-sm last:border-b-0"
                        >
                          <div>
                            <span className="font-medium text-cocoa">{student.full_name || student.name}</span>
                            {student.grade && (
                              <span className="ml-2 text-xs text-cocoa/60">• Grade {student.grade}</span>
                            )}
                          </div>
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-sand text-brick focus:ring-brick"
                            checked={selected}
                            onChange={() => handleToggleStudent(student)}
                          />
                        </label>
                      );
                    })
                  ) : (
                    <p className="p-3 text-sm text-cocoa/60">No students yet. Add learners from your class list.</p>
                  )}
                </div>

                {roster.length > 0 && (
                  <div className="space-y-3 rounded-3xl border border-sand bg-shell/40 p-4">
                    <p className="text-sm font-medium text-cocoa">Cast details</p>
                    <div className="space-y-4">
                      {roster.map((entry) => {
                        const rosterKey = entry.id || entry.full_name || "";
                        const traitValue = Array.isArray(entry.traits)
                          ? entry.traits.join(", ")
                          : entry.traits || "";
                        return (
                          <div key={rosterKey} className="space-y-3 rounded-2xl bg-white p-3 shadow-soft">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div>
                                <p className="font-semibold text-cocoa">{entry.full_name}</p>
                                {entry.grade && (
                                  <span className="text-xs text-cocoa/60">Grade {entry.grade}</span>
                                )}
                              </div>
                              <button
                                type="button"
                                className="text-xs text-rose-600 underline-offset-4 hover:underline"
                                onClick={() => handleRemoveRosterEntry(rosterKey)}
                              >
                                Remove
                              </button>
                            </div>
                            <div className="grid gap-2 sm:grid-cols-2">
                              <label className="text-xs text-cocoa/60">
                                Nickname
                                <input
                                  className="input mt-1"
                                  value={entry.nickname || ""}
                                  onChange={(event) =>
                                    handleRosterFieldChange(rosterKey, "nickname", event.target.value)
                                  }
                                  placeholder="Preferred name"
                                />
                              </label>
                              <label className="text-xs text-cocoa/60">
                                Role in story
                                <input
                                  className="input mt-1"
                                  value={entry.role || ""}
                                  onChange={(event) =>
                                    handleRosterFieldChange(rosterKey, "role", event.target.value)
                                  }
                                  placeholder="Leader, narrator, adventurer…"
                                />
                              </label>
                            </div>
                            <label className="text-xs text-cocoa/60">
                              Traits or highlights (comma separated)
                              <textarea
                                className="input mt-1 min-h-[60px]"
                                value={traitValue}
                                onChange={(event) =>
                                  handleRosterFieldChange(rosterKey, "traits", event.target.value)
                                }
                                placeholder="Curious, loves robotics, twin, soccer captain"
                              />
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </details>

            <details open className="rounded-3xl border border-sand bg-white shadow-soft lg:col-span-2">
              <summary className="flex cursor-pointer items-center justify-between gap-2 px-5 py-4 text-sm font-semibold text-cocoa [&::-webkit-details-marker]:hidden">
                <span>Pages</span>
                <span className="text-xs uppercase tracking-wide text-cocoa/40">Toggle</span>
              </summary>
              <div className="space-y-4 border-t border-sand/60 px-5 py-5">
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold text-cocoa">Page editor</h2>
                  <p className="text-sm text-cocoa/70">
                    Update text, captions, and prompts. Render illustrations one spread at a time or open a preview.
                  </p>
                </div>

                {pageThumbnails.length > 0 && (
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {pageThumbnails.map((thumb) => {
                      const thumbPage = thumb.page;
                      const thumbHasImage =
                        thumbPage?.illustration_ready ||
                        pickStorageKey(
                          thumbPage?.image_url,
                          thumbPage?.imageUrl,
                          thumbPage?.image_path,
                          thumbPage?.imagePath
                        ) ||
                        pickRemoteUrl(
                          thumbPage?.image_url,
                          thumbPage?.imageUrl,
                          thumbPage?.image_path,
                          thumbPage?.imagePath
                        );
                      return (
                        <button
                          key={thumb.key}
                          type="button"
                          className="group flex shrink-0 flex-col items-center gap-1 text-xs text-cocoa/70"
                          onClick={() => handlePreviewPage(thumb.pageNumber)}
                        >
                          <div
                            className={`relative h-20 w-16 overflow-hidden rounded-xl border ${
                              thumbHasImage
                                ? "border-sand bg-white"
                                : "border-dashed border-sand/70 bg-shell"
                            }`}
                          >
                            <PagePreview
                              page={thumbPage}
                              refreshTick={refreshTick}
                              imgClassName="h-full w-full object-cover transition group-hover:scale-105"
                              placeholderClassName="flex h-full items-center justify-center"
                              placeholderContent={
                                <span className="text-[10px] text-cocoa/60">Page {thumb.pageNumber}</span>
                              }
                            />
                          </div>
                          <span className="text-[10px] uppercase tracking-wide text-cocoa/60">
                            Page {thumb.pageNumber}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="space-y-3">
                  {(storybook.pages || []).length === 0 ? (
                    <p className="rounded-2xl bg-shell p-4 text-sm text-cocoa/70">
                      We’ll add pages after the narrative is generated.
                    </p>
                  ) : (
                    (storybook.pages || []).map((page, index) => {
                      const pageNumber = page?.pageNumber ?? index + 1;
                      const key = page?.pageId || page?.page_id || `page-${pageNumber}`;
                      const hasImage =
                        page?.illustration_ready ||
                        pickStorageKey(
                          page?.image_url,
                          page?.imageUrl,
                          page?.image_path,
                          page?.imagePath
                        ) ||
                        pickRemoteUrl(
                          page?.image_url,
                          page?.imageUrl,
                          page?.image_path,
                          page?.imagePath
                        );
                      const busy = Boolean(pageBusy?.[pageNumber]);
                      const traitsValue = typeof page?.prompt === "string" ? page.prompt : "";
                      return (
                        <div key={key} className="space-y-3 rounded-3xl border border-sand bg-shell/40 p-4">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className="h-14 w-10 overflow-hidden rounded-xl border border-sand bg-shell">
                                <PagePreview
                                  page={page}
                                  refreshTick={refreshTick}
                                  imgClassName="h-full w-full object-cover"
                                  placeholderClassName="flex h-full items-center justify-center"
                                  placeholderContent={
                                    <span className="text-[10px] text-cocoa/60">No art yet</span>
                                  }
                                />
                              </div>
                              <div>
                                <h3 className="text-sm font-semibold text-cocoa">Page {pageNumber}</h3>
                                <p className="text-xs text-cocoa/60">
                                  {hasImage ? "Illustration ready" : "Awaiting illustration"}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                className="btn-tertiary"
                                onClick={() => handlePreviewPage(pageNumber)}
                                disabled={!hasImage}
                              >
                                Preview
                              </button>
                            </div>
                          </div>
                          <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr),minmax(0,1fr)]">
                            <div className="space-y-3">
                              <textarea
                                className="input min-h-[160px]"
                                value={page.text || ""}
                                onChange={(event) =>
                                  handlePageChange(pageNumber, "text", event.target.value)
                                }
                              />
                              <input
                                type="text"
                                className="input"
                                placeholder="Optional caption"
                                value={page.caption || ""}
                                onChange={(event) =>
                                  handlePageChange(pageNumber, "caption", event.target.value)
                                }
                              />
                              <input
                                type="text"
                                className="input"
                                placeholder="Custom illustration prompt"
                                value={traitsValue}
                                onChange={(event) =>
                                  handlePageChange(pageNumber, "prompt", event.target.value)
                                }
                              />
                            </div>
                            <div className="flex flex-col gap-3">
                              <div className="overflow-hidden rounded-2xl border border-sand bg-white">
                                <PagePreview
                                  page={page}
                                  refreshTick={refreshTick}
                                  imgClassName="h-full w-full object-cover"
                                  placeholderClassName="flex h-full min-h-[160px] items-center justify-center"
                                  placeholderContent={
                                    <span className="text-xs text-cocoa/60">
                                      Illustration not generated yet.
                                    </span>
                                  }
                                />
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={() => handleRenderPage(pageNumber)}
                                  disabled={busy || actionLoading}
                                >
                                  {busy ? "Rendering…" : "Render page"}
                                </button>
                                <button
                                  type="button"
                                  className="btn-tertiary"
                                  onClick={() => handleRenderPage(pageNumber, { force: true })}
                                  disabled={busy || actionLoading}
                                >
                                  Force re-render
                                </button>
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={() => handlePreviewPage(pageNumber)}
                                  disabled={!hasImage}
                                >
                                  View preview
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </details>

            <details open className="rounded-3xl border border-sand bg-white shadow-soft lg:col-span-2">
              <summary className="flex cursor-pointer items-center justify-between gap-2 px-5 py-4 text-sm font-semibold text-cocoa [&::-webkit-details-marker]:hidden">
                <span>Media & actions</span>
                <span className="text-xs uppercase tracking-wide text-cocoa/40">Toggle</span>
              </summary>
              <div className="space-y-4 border-t border-sand/60 px-5 py-5">
                {storybook?.classroom_id && (
                  <PhotosPanel
                    classroomId={storybook.classroom_id}
                    bookId={storybook.id}
                    onUploaded={(items) => {
                      if (items.length) {
                        const count = items.length;
                        pushToast(
                          `Uploaded ${count} photo${count === 1 ? "" : "s"} — ready for characters!`,
                          "success"
                        );
                      }
                      refresh();
                    }}
                  />
                )}

                <div className="rounded-2xl bg-shell/60 p-3 text-xs text-cocoa/70">
                  {settings.renderMode === "per-page"
                    ? "Page-by-page mode is active. Use the controls inside each page to render and preview spreads."
                    : "One-button mode is active. Customize pages here, then generate text and illustrations in one flow."}
                </div>

                {storybook?.classroom_id && (
                  <div className="flex items-center gap-3">
                    <button className="btn-secondary" onClick={() => setCharactersOpen(true)}>
                      Build characters from photos
                    </button>
                    <p className="text-xs text-cocoa/60">Optional, but improves identity consistency.</p>
                  </div>
                )}

                <div className="space-y-3">
                  <h2 className="text-lg font-semibold text-cocoa">Actions</h2>

                  <div className="flex flex-col gap-3">
                    <button type="button" className="btn" onClick={() => persistStorybook()} disabled={saveLoading}>
                      {saveLoading ? "Saving…" : "Save draft"}
                    </button>
                    <button type="button" className="btn-secondary" onClick={handleGenerateText} disabled={actionLoading}>
                      Regenerate text
                    </button>
                    <button
                      type="button"
                      className="btn-tertiary"
                      onClick={() => oneButtonFlow({ force: true })}
                      disabled={actionLoading}
                    >
                      Re-run one-button flow
                    </button>
                    <button type="button" className="btn-secondary" onClick={handleRenderPdf} disabled={actionLoading}>
                      Render PDF
                    </button>
                    {storybook.pdf_url && (
                      <div className="rounded-2xl bg-shell p-3 text-xs text-cocoa/70">
                        Latest PDF stored at <code>{storybook.pdf_url}</code>
                      </div>
                    )}
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handleSubmitDraft}
                      disabled={storybook.status === "submitted"}
                    >
                      Submit for review
                    </button>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setConsentOpen(true)}
                      disabled={!canSubmit || isPublished}
                    >
                      Publish storybook
                    </button>
                    {isPublished && (
                      <p className="rounded-2xl bg-emerald-50 p-3 text-xs text-emerald-700">
                        Published{" "}
                        {storybook.published_at
                          ? new Date(storybook.published_at).toLocaleString()
                          : "just now"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </details>
          </div>
        </div>
      )}

      <ConsentModal
        open={consentOpen}
        onClose={() => setConsentOpen(false)}
        onConfirm={handlePublish}
        consent={consentState}
        setConsent={setConsentState}
      />
      <CharactersModal
        open={charactersOpen}
        onClose={() => setCharactersOpen(false)}
        classroomId={storybook?.classroom_id || classroom?.id}
        onSaved={() => {
          // characters saved → your illustration job will pick them up
          refresh();
        }}
      />
      <StoryPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        pages={storybook?.pages || []}
        initialIndex={previewIndex}
        title="Storybook preview"
        refreshTick={refreshTick}
        progress={activeProgress}
      />
      <JobProgressModal open={showProgress} job={job} onClose={() => setShowProgress(false)} />
    </div>
  );
}
